import { supabase } from '../supabase';
import { recordOpportunityActivity } from '../opportunityActivities';

export type OpportunityStatus = 'Open' | 'Won' | 'Lost';

/**
 * Marks an opportunity Open / Won / Lost and logs the `status_changed` activity,
 * mirroring what the CRM board and the control-centre snippet do inline.
 *
 * `lost_reason` is stored when losing (and cleared on any other status) so a
 * re-opened lead doesn't keep a stale reason. DB triggers handle the rest of the
 * side effects (e.g. syncing the sales pin marker).
 *
 * The status update is awaited; the activity log is best-effort so a logging
 * failure never leaves the caller thinking the status change failed.
 */
export async function setOpportunityStatus(params: {
  businessId: string;
  opportunityId: number;
  customerId?: string | null;
  fromStatus?: string | null;
  toStatus: OpportunityStatus;
  lostReason?: string | null;
}): Promise<void> {
  const {
    businessId,
    opportunityId,
    customerId,
    fromStatus = null,
    toStatus,
    lostReason = null,
  } = params;

  if (!supabase || !businessId || !opportunityId) {
    throw new Error('setOpportunityStatus: missing required parameters');
  }
  if (fromStatus && fromStatus === toStatus) return;

  const { error } = await supabase
    .from('pipeline_opportunities')
    .update({
      status: toStatus,
      updated_at: new Date().toISOString(),
      lost_reason: toStatus === 'Lost' ? lostReason : null,
    })
    .eq('id', opportunityId);
  if (error) throw error;

  if (customerId) {
    recordOpportunityActivity({
      businessId,
      customerId,
      activityType: 'status_changed',
      activityData: { from: fromStatus, to: toStatus },
      specificOpportunityId: opportunityId,
    }).catch((err) => {
      console.error('[setOpportunityStatus] Error recording activity:', err);
    });
  }
}

/** Whether this business wants a reason recorded when a lead is marked Lost. */
export async function isLostReasonRequired(businessId: string): Promise<boolean> {
  if (!supabase || !businessId) return false;
  try {
    const { data } = await supabase.rpc('get_app_setting', {
      p_business_id: businessId,
      p_app_slug: 'crm',
      p_setting_key: 'record_lost_reason',
      p_default_value: true,
    });
    return data === null ? true : !!data;
  } catch (err) {
    console.error('[isLostReasonRequired] Error loading setting:', err);
    return true;
  }
}
